import ExpandMore from "@mui/icons-material/ExpandMore";
import OpenInNew from "@mui/icons-material/OpenInNew";
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { getStatusLabel } from "encamp-shared/src/submissions";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  GeneratorStatusExceedanceStatus,
  StaffGeneratorStatusChangeStats,
  StaffGeneratorStatusChangesQuery,
  StaffGeneratorStatusChangesQueryVariables,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useOmnisearchDatagrid } from "hooks/useOmnisearchDatagrid";
import { GET_STAFF_GENERATOR_STATUS_CHANGES } from "hooks/useStaffGeneratorStatusChanges";
import { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { KeyMetric } from "routes/Staff/Fulfillment/WorkflowOverviewStats/KeyMetric";

import { brandColors } from "../../../util";
import {
  CreateGeneratorStatusChangeSubmissionsDialog,
  GeneratorStatusChangeFacility,
} from "./CreateGeneratorStatusChangeSubmissionsDialog";
import { ExceedanceActionDrawer, ExceedanceForDrawer } from "./ExceedanceActionDrawer";
import { ExceedanceStatusChip } from "./ExceedanceStatusChip";

// The grid row id is composite. `useGridSelection` resolves the selection model
// back to row objects by `row.id` (not the grid's getRowId), so each row must
// carry an explicit `id` for bulk selection to populate.
type StatusChangeRow =
  StaffGeneratorStatusChangesQuery["staffGeneratorStatusChanges"]["items"][number] & {
    id: string;
  };

function getRowId(row: Pick<StatusChangeRow, "facilityId" | "source" | "periodStart">): string {
  return `${row.facilityId}-${row.source}-${row.periodStart}`;
}

function toDrawerExceedance(row: StatusChangeRow): ExceedanceForDrawer {
  return {
    facilityId: row.facilityId,
    tenantId: row.tenantId,
    facilityName: row.facilityName,
    organization: row.tenantName,
    state: row.state ?? null,
    source: row.source,
    periodStart: row.periodStart,
    reportedFederalStatus: row.reportedFederalStatus,
    calculatedFederalStatus: row.calculatedFederalStatus,
    changeExplanation: row.changeExplanation,
    resolutionStatus: row.resolutionStatus,
    openSubmission: row.openSubmission,
    linkedSubmission: row.linkedSubmission,
  };
}

const STATUS_RANK: Record<string, number> = {
  VERY_SMALL_QUANTITY_GENERATOR: 1,
  SMALL_QUANTITY_GENERATOR: 2,
  LARGE_QUANTITY_GENERATOR: 3,
};

function getBgColor({
  escalated,
  downgraded,
  isLight,
}: {
  escalated: boolean;
  downgraded: boolean;
  isLight: boolean;
}): string {
  if (escalated) return isLight ? "rgba(211, 47, 47, 0.06)" : "rgba(244, 67, 54, 0.08)";
  if (downgraded) return isLight ? "rgba(46, 125, 50, 0.06)" : "rgba(76, 175, 80, 0.08)";
  return isLight ? "rgba(237, 108, 2, 0.06)" : "rgba(255, 152, 0, 0.08)";
}

function StatusTransition({
  oldStatus,
  newStatus,
}: {
  oldStatus: string | null | undefined;
  newStatus: string | null | undefined;
}) {
  const theme = useTheme();
  const changed = oldStatus !== newStatus;
  const oldRank = oldStatus ? (STATUS_RANK[oldStatus] ?? 0) : 0;
  const newRank = newStatus ? (STATUS_RANK[newStatus] ?? 0) : 0;
  const escalated = changed && newRank > oldRank;
  const downgraded = changed && newRank < oldRank;

  const oldLabel = oldStatus ? prettyPrintEnumValue(oldStatus) : "—";
  const newLabel = newStatus ? prettyPrintEnumValue(newStatus) : "—";

  if (!changed) {
    return (
      <Typography variant="body2" color="text.secondary" noWrap>
        {newLabel}
      </Typography>
    );
  }

  const bgColor = getBgColor({ escalated, downgraded, isLight: theme.palette.mode === "light" });

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        backgroundColor: bgColor,
        borderRadius: 1,
        px: 0.75,
        py: 0.25,
        maxWidth: "100%",
      }}
    >
      <Typography variant="body2" noWrap>
        {oldLabel}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        →
      </Typography>
      <Typography variant="body2" fontWeight={600} noWrap>
        {newLabel}
      </Typography>
    </Box>
  );
}

function defaultStartDate(): DateTime {
  return DateTime.now().minus({ months: 3 }).startOf("month");
}

function formatDateForApi(dt: DateTime | null): string | undefined {
  if (!dt) return undefined;
  return dt.toFormat("yyyy-MM-dd");
}

/**
 * Bulk-actions dropdown for the exceedances grid, mirroring the Fulfillment
 * "Bulk Actions" menu: disabled until rows are selected, no "N selected" text.
 */
function BulkActionsMenu({
  disabled,
  onCreateNotifications,
}: {
  disabled: boolean;
  onCreateNotifications: () => void;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  return (
    <>
      <Button
        size="medium"
        variant="contained"
        disabled={disabled}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        endIcon={<ExpandMore />}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        sx={{
          whiteSpace: "nowrap",
          backgroundColor: brandColors["encamp-orange"][500],
          color: "white",
          "&:hover": { backgroundColor: brandColors["encamp-orange"][700] },
        }}
      >
        Bulk Actions
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            onCreateNotifications();
          }}
        >
          Create Generator Status Change Notifications
        </MenuItem>
      </Menu>
    </>
  );
}

export const GeneratorStatusChanges = () => {
  useBreadcrumb({ label: "Generator Status Changes - Potential Discrepancies" });

  const [startDate, setStartDate] = useState<DateTime | null>(defaultStartDate());
  const [endDate, setEndDate] = useState<DateTime | null>(null);

  const dateVars = useMemo(
    () => ({
      startDate: formatDateForApi(startDate),
      endDate: formatDateForApi(endDate),
    }),
    [startDate, endDate]
  );

  const { createUrlFromOmnisearchKeyValues } = useOmnisearchDatagrid({
    persistenceKey: "staffGeneratorStatusChanges",
    columns: [],
  });

  const [stats, setStats] = useState<StaffGeneratorStatusChangeStats | undefined>(undefined);
  const [statsLoading, setStatsLoading] = useState(true);

  const [selectedRows, setSelectedRows] = useState<StatusChangeRow[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedExceedance, setSelectedExceedance] = useState<ExceedanceForDrawer | null>(null);

  const handleSelectedRowsChanged = useCallback((rows: StatusChangeRow[]) => {
    setSelectedRows(rows);
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedRows([]);
  }, []);

  // One notification per facility — collapse multiple exceedance rows for the
  // same facility into a single selection.
  const selectedFacilities = useMemo<GeneratorStatusChangeFacility[]>(() => {
    const byFacilityId = new Map<string, GeneratorStatusChangeFacility>();
    for (const row of selectedRows) {
      if (!byFacilityId.has(row.facilityId)) {
        byFacilityId.set(row.facilityId, {
          facilityId: row.facilityId,
          tenantId: row.tenantId,
          facilityName: row.facilityName,
          organization: row.tenantName,
          state: row.state ?? null,
          periodStart: row.periodStart,
        });
      }
    }
    return [...byFacilityId.values()];
  }, [selectedRows]);

  const selectedIds = useMemo(() => selectedRows.map(getRowId), [selectedRows]);

  const commandButtons = useMemo(
    () => [
      <BulkActionsMenu
        key="gsc-bulk-actions"
        disabled={selectedFacilities.length === 0}
        onCreateNotifications={() => setDialogOpen(true)}
      />,
    ],
    [selectedFacilities.length]
  );

  // Stable references so OmnisearchDataDisplay's items/count memo only recomputes
  // when the query data changes — not on every render. The .map() injects a row
  // id (see StatusChangeRow) and would otherwise return a fresh array each render,
  // forcing the grid to re-render continuously.
  const getItems = useCallback(
    (data: StaffGeneratorStatusChangesQuery) =>
      data.staffGeneratorStatusChanges.items.map((item) => ({ ...item, id: getRowId(item) })),
    []
  );

  const getCount = useCallback(
    (data: StaffGeneratorStatusChangesQuery) => data.staffGeneratorStatusChanges.count,
    []
  );

  const handleQueryStateChange = useCallback(
    ({
      data,
      loading,
    }: {
      data: StaffGeneratorStatusChangesQuery | undefined;
      loading: boolean;
    }) => {
      setStats(data?.staffGeneratorStatusChanges.stats);
      setStatsLoading(loading);
    },
    []
  );

  const handleStartDateChange = useCallback((value: DateTime | null) => {
    setStartDate(value);
  }, []);

  const handleEndDateChange = useCallback((value: DateTime | null) => {
    setEndDate(value);
  }, []);

  return (
    <Stack py={3} spacing={2}>
      {/* Date Range Filter */}
      <Paper elevation={0} sx={{ px: 3, py: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="subtitle2" color="text.secondary">
            Period Range:
          </Typography>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <DatePicker
              label="Start"
              value={startDate}
              onChange={handleStartDateChange}
              views={["year", "month"]}
              slotProps={{
                textField: {
                  variant: "standard",
                  size: "small",
                  sx: { width: 150 },
                },
              }}
            />
            <DatePicker
              label="End"
              value={endDate}
              onChange={handleEndDateChange}
              views={["year", "month"]}
              slotProps={{
                textField: {
                  variant: "standard",
                  size: "small",
                  sx: { width: 150 },
                  placeholder: "Current",
                },
              }}
            />
          </LocalizationProvider>
        </Stack>
      </Paper>

      {/* Stats Cards — px:3 matches the grid/search wrapper inset (OmnisearchDataGrid
          withPadding → px: 3), so the filter, stat cards, and grid share left/right edges. */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, px: 3 }}>
        <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
          <KeyMetric
            name="Potential Discrepancies"
            value={stats?.potentialExceedances}
            loading={statsLoading}
            href={createUrlFromOmnisearchKeyValues({
              keyValues: [],
              keysToKeep: [],
            })}
          />
        </Box>
        <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
          <KeyMetric
            name="Facilities Affected"
            value={stats?.facilitiesChanged}
            loading={statsLoading}
            href={createUrlFromOmnisearchKeyValues({
              keyValues: [],
              keysToKeep: [],
            })}
          />
        </Box>
        <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
          <KeyMetric
            name="Calculated as LQG"
            value={stats?.exceedancesToLqg}
            loading={statsLoading}
            href={createUrlFromOmnisearchKeyValues({
              keyValues: [["calculatedFederalStatus", "LARGE_QUANTITY_GENERATOR"]],
              keysToKeep: [],
            })}
          />
        </Box>
        <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
          <KeyMetric
            name="Calculated as SQG"
            value={stats?.exceedancesToSqg}
            loading={statsLoading}
            href={createUrlFromOmnisearchKeyValues({
              keyValues: [["calculatedFederalStatus", "SMALL_QUANTITY_GENERATOR"]],
              keysToKeep: [],
            })}
          />
        </Box>
      </Box>

      {/* Data Grid */}
      <OmnisearchDataGrid<
        StaffGeneratorStatusChangesQuery,
        StaffGeneratorStatusChangesQueryVariables,
        StatusChangeRow
      >
        persistenceKey="staffGeneratorStatusChanges"
        isURLDriven
        dataQueryVariables={dateVars as StaffGeneratorStatusChangesQueryVariables}
        onQueryStateChange={handleQueryStateChange}
        getRowId={getRowId}
        checkboxSelection
        disableRowSelectionOnClick
        onSelectedRowsChanged={handleSelectedRowsChanged}
        selectedIds={selectedIds}
        // Only unresolved exceedances are eligible for bulk "File notification" —
        // anything pending/resolved/ignored already has a disposition.
        isRowSelectable={(params) =>
          params.row.resolutionStatus === GeneratorStatusExceedanceStatus.Unresolved
        }
        commandButtons={commandButtons}
        onRowClick={(params) => setSelectedExceedance(toDrawerExceedance(params.row))}
        columns={[
          {
            // Field is "tenant" (not "tenantName") so the Organization filter is a
            // tenant dropdown (multipleTenant) instead of free text. The multipleTenant
            // picker emits the term keyed "tenantId", which the resolver matches with a
            // "tenantId"::text IN filter (alias "tenant").
            field: "tenant",
            headerName: "Organization",
            flex: 0.6,
            filterKeyType: "multipleTenant",
            valueGetter: (_, row) => row.tenantName,
          },
          {
            field: "facilityName",
            headerName: "Facility",
            flex: 0.6,
            renderCell({ row }) {
              return (
                <Link
                  to={`/o/${row.tenantId}/waste/facilities/${row.facilityId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "inherit",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <OpenInNew fontSize="small" />
                  {row.facilityName}
                </Link>
              );
            },
          },
          {
            field: "state",
            headerName: "State/Territory",
            flex: 0.3,
          },
          {
            field: "source",
            headerName: "Source",
            flex: 0.3,
            filterKeyType: "enum",
            enumValues: ["eManifest", "vendor"],
          },
          {
            field: "periodStart",
            headerName: "Period",
            flex: 0.3,
            valueGetter(_, row) {
              // periodStart is a date-only month value; render in UTC so the
              // first-of-month doesn't shift to the prior month in the viewer's
              // local timezone (e.g. Apr 1 UTC → Mar 31 in US Central).
              return new Date(row.periodStart).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                timeZone: "UTC",
              });
            },
          },
          {
            field: "exceedance",
            headerName: "Discrepancy",
            flex: 1,
            sortable: false,
            renderCell({ row }) {
              return (
                <StatusTransition
                  oldStatus={row.reportedFederalStatus}
                  newStatus={row.calculatedFederalStatus}
                />
              );
            },
            valueGetter(_, row) {
              const reportedLabel = row.reportedFederalStatus
                ? prettyPrintEnumValue(row.reportedFederalStatus)
                : "—";
              const calcLabel = row.calculatedFederalStatus
                ? prettyPrintEnumValue(row.calculatedFederalStatus)
                : "—";
              return `${reportedLabel} → ${calcLabel}`;
            },
          },
          {
            field: "changeExplanation",
            headerName: "Explanation",
            flex: 1,
            sortable: false,
            renderCell({ row }) {
              return (
                <Tooltip title={row.changeExplanation} placement="top-start">
                  <Typography variant="body2" noWrap sx={{ lineHeight: "inherit" }}>
                    {row.changeExplanation}
                  </Typography>
                </Tooltip>
              );
            },
          },
          {
            field: "updatedAt",
            headerName: "Calculated At",
            flex: 0.4,
            valueGetter(_, row) {
              return new Date(row.updatedAt).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              });
            },
          },
          {
            field: "stateStatus",
            headerName: "State Status",
            flex: 0.8,
            sortable: false,
            renderCell({ row }) {
              return (
                <StatusTransition oldStatus={row.oldStateStatus} newStatus={row.newStateStatus} />
              );
            },
            valueGetter(_, row) {
              const oldLabel = row.oldStateStatus ? prettyPrintEnumValue(row.oldStateStatus) : "—";
              const newLabel = row.newStateStatus ? prettyPrintEnumValue(row.newStateStatus) : "—";
              return row.oldStateStatus !== row.newStateStatus
                ? `${oldLabel} → ${newLabel}`
                : newLabel;
            },
          },
          {
            field: "resolutionStatus",
            headerName: "Status",
            flex: 0.45,
            sortable: false,
            filterKeyType: "enum",
            enumValues: ["UNRESOLVED", "PENDING", "RESOLVED", "IGNORED"],
            renderCell({ row }) {
              // When a submission is driving the status, surface its live state
              // (e.g. "In Review") instead of a flat "Pending" so staff can see
              // how far along the linked filing is.
              const sub = row.linkedSubmission ?? row.openSubmission;
              const isPending = row.resolutionStatus === GeneratorStatusExceedanceStatus.Pending;
              const label =
                isPending && sub
                  ? getStatusLabel(sub.status)
                  : prettyPrintEnumValue(row.resolutionStatus);
              return <ExceedanceStatusChip status={row.resolutionStatus} label={label} />;
            },
          },
          // Who currently owns the linked/open submission, so staff can see at a
          // glance who's working it (or that it's unassigned).
          {
            field: "assignee",
            headerName: "Assignee",
            flex: 0.45,
            sortable: false,
            renderCell({ row }) {
              const sub = row.linkedSubmission ?? row.openSubmission;
              const label = sub ? (sub.assigneeName ?? "Unassigned") : "—";
              return (
                <Typography variant="body2" color="text.secondary" noWrap>
                  {label}
                </Typography>
              );
            },
          },
          // Submission is the rightmost column so the linked-submission link for
          // each exceedance is always in a predictable, easy-to-find place. Prefer
          // an explicitly linked submission (any type) over the auto-detected
          // open notification.
          {
            field: "openSubmission",
            headerName: "Submission",
            flex: 0.5,
            sortable: false,
            renderCell({ row }) {
              const sub = row.linkedSubmission ?? row.openSubmission;
              if (!sub) {
                return (
                  <Typography variant="body2" color="text.secondary">
                    —
                  </Typography>
                );
              }
              const statusLabel = getStatusLabel(sub.status);
              const due = sub.dueDate
                ? ` · due ${new Date(sub.dueDate).toLocaleDateString("en-US")}`
                : "";
              return (
                <Tooltip title={`${sub.title} (${statusLabel}${due})`} placement="top-start">
                  <Link
                    to={`/staff/fulfillment/waste/submissions/${sub.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "inherit",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <OpenInNew fontSize="small" />
                    {statusLabel}
                  </Link>
                </Tooltip>
              );
            },
          },
        ]}
        columnVisibilityModel={{
          stateStatus: false,
        }}
        dataQuery={GET_STAFF_GENERATOR_STATUS_CHANGES}
        getItems={getItems}
        getCount={getCount}
        excludeFilterColumns={[
          "periodStart",
          "changeExplanation",
          "updatedAt",
          "exceedance",
          "stateStatus",
          "assignee",
          "openSubmission",
        ]}
      />

      <CreateGeneratorStatusChangeSubmissionsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        facilities={selectedFacilities}
        onSuccess={handleClearSelection}
      />

      <ExceedanceActionDrawer
        exceedance={selectedExceedance}
        onClose={() => setSelectedExceedance(null)}
        onResolved={() => setSelectedExceedance(null)}
      />
    </Stack>
  );
};
